import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { HashLink } from "react-router-hash-link";
import logo from "../images/logos/unphackathonLogo.png";
import logoWhite from "../images/logos/unphakatonIcon_white.png";
import logoUniverziteta from "../images/logos/logoUniverziteta.png";
import logoUniverzitetaWhite from "../images/logos/logoUniverziteta_white.png";

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease;

  background-color: ${(props) =>
    props.$scrolled ? "var(--background)" : "transparent"};
  box-shadow: ${(props) =>
    props.$scrolled ? "0 4px 20px rgba(30, 64, 175, 0.1)" : "none"};
  backdrop-filter: ${(props) => (props.$scrolled ? "blur(10px)" : "none")};
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled(HashLink)`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  color: var(--text);

  span {
    color: var(--primary);
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;
const LogoImage = styled.img`
  width: 50px;
  @media (max-width: 768px) {
    width: 35px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(HashLink)`
  color: var(--text);
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  transition: color 0.2s ease;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    transition: width 0.2s ease;
  }

  &:hover:after,
  &.active:after {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(30, 64, 175, 0.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background);
  z-index: 200;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  transform: ${(props) =>
    props.$isOpen ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.3s ease;
  backdrop-filter: blur(10px);
`;

const MobileNavLink = styled(HashLink)`
  color: var(--text);
  font-size: 1.5rem;
  font-weight: 600;
  padding: 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(30, 64, 175, 0.1);
    transform: translateY(-2px);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: none;
  border: none;
  color: var(--text);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(30, 64, 175, 0.1);
    transform: rotate(90deg);
  }
`;

const ThemeToggle = styled.button`
  background: none;
  border: none;
  color: var(--text);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(30, 64, 175, 0.1);
    transform: scale(1.1);
  }
`;

function Navbar({ isDarkMode, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Nav $scrolled={scrolled}>
        <NavContainer>
          <Logo to="#pocetna" smooth>
            {isDarkMode ? (
              <>
                <LogoImage src={logoUniverzitetaWhite} alt="logo" />
                <LogoImage src={logoWhite} alt="logo" />
              </>
            ) : (
              <>
                <LogoImage src={logoUniverziteta} alt="logo" />
                <LogoImage src={logo} alt="logo" />
              </>
            )}
            UNINP<span>HACKATHON</span>
          </Logo>

          <ButtonContainer>
            <NavLinks>
              <NavLink to="#pocetna" smooth>
                Početna
              </NavLink>
              <NavLink to="#o-nama" smooth>
                O Nama
              </NavLink>
              <NavLink to="#kalendar" smooth>
                Kalendar
              </NavLink>
              <NavLink to="#team" smooth>
                Naš Tim
              </NavLink>
              <NavLink to="#sponzori" smooth>
                Partneri
              </NavLink>
              <NavLink
                to="https://docs.google.com/forms/d/e/1FAIpQLSezOcMfLSszThuY6JAKwXA6ALvLxI1abH3hgyc0poeX_tk_PQ/viewform"
                target="_blank"
                rel="noopener noreferrer"
                smooth>
                Prijavi se
              </NavLink>
            </NavLinks>

            <ThemeToggle onClick={toggleTheme} aria-label="Toggle theme">
              {isDarkMode ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </ThemeToggle>

            <MobileMenuButton onClick={toggleMobileMenu}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </MobileMenuButton>
          </ButtonContainer>
        </NavContainer>
      </Nav>

      <MobileMenu $isOpen={mobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </CloseButton>

        <MobileNavLink to="#pocetna" smooth onClick={closeMobileMenu}>
          Početna
        </MobileNavLink>
        <MobileNavLink to="#o-nama" smooth onClick={closeMobileMenu}>
          O Nama
        </MobileNavLink>
        <MobileNavLink to="#kalendar" smooth onClick={closeMobileMenu}>
          Radionice
        </MobileNavLink>
        <MobileNavLink to="#team" smooth onClick={closeMobileMenu}>
          Naš Tim
        </MobileNavLink>
        <MobileNavLink to="#sponzori" smooth onClick={closeMobileMenu}>
          Partneri
        </MobileNavLink>
        <MobileNavLink
          to="https://docs.google.com/forms/d/e/1FAIpQLSezOcMfLSszThuY6JAKwXA6ALvLxI1abH3hgyc0poeX_tk_PQ/viewform"
          target="_blank"
          rel="noopener noreferrer"
          smooth
          onClick={closeMobileMenu}>
          Prijavi se
        </MobileNavLink>

        <ThemeToggle
          onClick={toggleTheme}
          aria-label="Toggle theme"
          style={{ marginTop: "1rem" }}>
          {isDarkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </ThemeToggle>
      </MobileMenu>
    </>
  );
}

export default Navbar;
